import React, { useState, useEffect } from 'react';
import './AccessibilityPanel.css';

const AccessibilityPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [highContrast, setHighContrast] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [screenReaderMode, setScreenReaderMode] = useState(false);

  useEffect(() => {
    // Apply accessibility settings to document
    document.documentElement.style.fontSize = `${fontSize}px`;
    
    if (highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    
    if (reducedMotion) {
      document.body.classList.add('reduced-motion');
    } else {
      document.body.classList.remove('reduced-motion');
    }
    
    if (screenReaderMode) {
      document.body.classList.add('screen-reader-mode');
    } else {
      document.body.classList.remove('screen-reader-mode');
    }
  }, [fontSize, highContrast, reducedMotion, screenReaderMode]);

  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  const increaseFontSize = () => {
    setFontSize(prev => Math.min(prev + 2, 24));
  };

  const decreaseFontSize = () => {
    setFontSize(prev => Math.max(prev - 2, 12));
  };

  const resetFontSize = () => {
    setFontSize(16);
  };

  const announceToScreenReader = (message) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  const handleAccessibilityChange = (setting, value, message) => {
    switch (setting) {
      case 'fontSize':
        setFontSize(value);
        announceToScreenReader(`Font size changed to ${value} pixels`);
        break;
      case 'highContrast':
        setHighContrast(value);
        announceToScreenReader(value ? 'High contrast mode enabled' : 'High contrast mode disabled');
        break;
      case 'reducedMotion':
        setReducedMotion(value);
        announceToScreenReader(value ? 'Reduced motion enabled' : 'Reduced motion disabled');
        break;
      case 'screenReaderMode':
        setScreenReaderMode(value);
        announceToScreenReader(value ? 'Screen reader mode enabled' : 'Screen reader mode disabled');
        break;
    }
  };

  return (
    <>
      {/* Accessibility Toggle Button */}
      <button
        className={`accessibility-toggle ${isOpen ? 'open' : ''}`}
        onClick={togglePanel}
        aria-label={isOpen ? 'Close accessibility panel' : 'Open accessibility panel'}
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
      >
        <span className="accessibility-icon" aria-hidden="true">
          {isOpen ? '✕' : <span className="material-symbols-outlined">accessibility_new</span>}
        </span>
        <span className="sr-only">
          {isOpen ? 'Close accessibility options' : 'Open accessibility options'}
        </span>
      </button>

      {/* Accessibility Panel */}
      <div
        id="accessibility-panel"
        className={`accessibility-panel ${isOpen ? 'open' : ''}`}
        role="region"
        aria-label="Accessibility options"
        aria-hidden={!isOpen}
      >
        <div className="accessibility-header">
          <h2>Accessibility Options</h2>
          <button
            className="close-button"
            onClick={togglePanel}
            aria-label="Close accessibility panel"
          >
            ×
          </button>
        </div>

        <div className="accessibility-content">
          {/* Font Size Controls */}
          <div className="accessibility-section">
            <h3>Text Size</h3>
            <div className="font-size-controls">
              <button
                onClick={() => handleAccessibilityChange('fontSize', fontSize - 2, 'Decreased font size')}
                aria-label="Decrease font size"
                disabled={fontSize <= 12}
              >
                A-
              </button>
              <span className="font-size-display" aria-live="polite">
                {fontSize}px
              </span>
              <button
                onClick={() => handleAccessibilityChange('fontSize', fontSize + 2, 'Increased font size')}
                aria-label="Increase font size"
                disabled={fontSize >= 24}
              >
                A+
              </button>
              <button
                onClick={() => handleAccessibilityChange('fontSize', 16, 'Reset font size')}
                aria-label="Reset font size to default"
                className="reset-button"
              >
                Reset
              </button>
            </div>
          </div>

          {/* High Contrast Toggle */}
          <div className="accessibility-section">
            <h3>Visual Options</h3>
            <label className="accessibility-toggle-label">
              <input
                type="checkbox"
                checked={highContrast}
                onChange={(e) => handleAccessibilityChange('highContrast', e.target.checked)}
                aria-describedby="high-contrast-description"
              />
              <span className="toggle-text">High Contrast</span>
            </label>
            <p id="high-contrast-description" className="accessibility-description">
              Increases color contrast for better visibility
            </p>
          </div>

          {/* Reduced Motion Toggle */}
          <div className="accessibility-section">
            <label className="accessibility-toggle-label">
              <input
                type="checkbox"
                checked={reducedMotion}
                onChange={(e) => handleAccessibilityChange('reducedMotion', e.target.checked)}
                aria-describedby="reduced-motion-description"
              />
              <span className="toggle-text">Reduce Motion</span>
            </label>
            <p id="reduced-motion-description" className="accessibility-description">
              Reduces animations and transitions
            </p>
          </div>

          {/* Screen Reader Mode */}
          <div className="accessibility-section">
            <label className="accessibility-toggle-label">
              <input
                type="checkbox"
                checked={screenReaderMode}
                onChange={(e) => handleAccessibilityChange('screenReaderMode', e.target.checked)}
                aria-describedby="screen-reader-description"
              />
              <span className="toggle-text">Screen Reader Mode</span>
            </label>
            <p id="screen-reader-description" className="accessibility-description">
              Optimizes layout for screen readers
            </p>
          </div>

          {/* Keyboard Navigation Help */}
          <div className="accessibility-section">
            <h3>Keyboard Navigation</h3>
            <div className="keyboard-help">
              <p>Use Tab to navigate, Enter to activate, and Escape to close panels.</p>
              <p>Arrow keys can be used to navigate within menus and lists.</p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="accessibility-section">
            <h3>Accessibility Support</h3>
            <p className="accessibility-description">
              Having trouble accessing our website? Contact us for assistance:
            </p>
            <a 
              href="mailto:accessibility@rdvsystems.com" 
              className="accessibility-contact"
              aria-label="Send email to accessibility support"
            >
              accessibility@rdvsystems.com
            </a>
            <div className="accessibility-links">
              <a 
                href="/accessibility-statement" 
                className="accessibility-link"
                aria-label="Read our full accessibility statement"
              >
                Read Accessibility Statement
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Screen Reader Only Content */}
      <div className="sr-only">
        <h1>RDV Systems - Infrastructure 3D Safety & Visualization Software</h1>
        <p>
          Advanced infrastructure safety analysis and visualization platform. 
          Monitor, analyze, detect, and report on critical infrastructure elements 
          with cutting-edge 3D AI technology.
        </p>
      </div>
    </>
  );
};

export default AccessibilityPanel;
